import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useForm} from "react-hook-form";
import {Autocomplete, FormLabel, Grid} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React from "react"
import Box from "@mui/material/Box";
import {useAllDict} from "@/store/dict.c";
import {DictPO} from "@/model/system";
import {useDrawerModal, useGlobalActions} from "@/store/global.c";
import {SearchData} from "@/widget/SearchData/SearchData";
import CurrencyYuanOutlinedIcon from '@mui/icons-material/CurrencyYuanOutlined';
import {DatePickers} from "@/theme/DatePickers";
import DatePicker from "react-datepicker";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {emptyValue, iso2Date} from "@/helper/method";
import {CustomerPO} from "@/model/customer";
import {SearchContractVO} from "@/service/contract.s";
import {RoleUserVO} from "@/service/user.s";
import {useRoleUsers} from "@/store/user.c";
import {useCreateTrademark, useQueryTrademark, useUpdateTrademark} from "@/service/trademark.s";
import {isNumber} from "lodash";
import isEmpty from "lodash/isEmpty";
import {InputFile} from "@/widget/UploadFile/InputFile";
import {uploadFileSchema} from "@/widget/UploadFile/WriteFile";
import {UploadFileVO} from "@/service/common.s";

const validationSchema = z.object({
    id: z.number().default(0),
    customerId: z.number().default(0),
    pattern: z.string().nullable(),
    trademarkName: z.string({message: "请填写商标名称"}).nonempty({message: "请填写商标名称"}),
    applyNo: z.string({message: "请填写登记号"}),
    applyDate: z.date({message: "请选择申请日期"}),
    customerName: z.string({message: "请选择关联客户"}).nonempty({message: "请选择关联客户"}),
    writer: z.unknown(),
    writerId: z.number({message: "请填写撰稿人"}),
    writeStatus: z.string({message: "请选择撰写状态"}).nonempty({message: "请选择撰写状态"}),
    writeStatusId: z.unknown(),
    price: z.unknown(),

    kind: z.string({message: "请选择服务类型"}).nonempty({message: "请选择服务类型"}),
    kindId: z.unknown(),

    registerDate: z.unknown().default(null),
    startRightsDate: z.unknown().default(null),
    endRightsDate: z.unknown().default(null),
    initialDate: z.unknown().default(null),
    initialNo: z.unknown(),
    registerNo: z.unknown(),
    category: z.string({message: "请填写申请类别"}).nonempty({message: "请填写申请类别"}),
    categoryId: z.unknown(),
    scope: z.string({message: "商品或服务项目"}).nullable(),
    explain: z.unknown(),
    contractId: z.unknown().default(0),
    contractName: z.unknown(),
    insideDeadline: z.unknown().default(null),
    sellerId:
        z.number({message: "请选择业务人员"}),
    assigner:
        z.unknown(),
    assignerId:
        z.number({message: "请选择分配人"}).optional(),
    seller:
        z.unknown(),
    file:
        z.array(uploadFileSchema),
    tempWriter:
        z.object({username: z.string().default(''), id: z.number()}).optional(),
    tempSeller: z.object({username: z.string().default(''), id: z.number()}).optional(),
    trademarkStatus: z.string({message: "请选择商标状态"}).nonempty({message: "请选择商标状态"}),
    trademarkStatusId: z.unknown(),
    rightsDate:
        z.array(z.date().or(z.literal(null)).default(null), z.date().or(z.literal(null)).default(null)),
    tempAssigner: z.object({username: z.string().default(''), id: z.number().default(0)}).optional(),
})

export type WriteTrademarkForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

export const WriteTrademark = () => {
    const allDict = useAllDict()
    const seller = useRoleUsers(["marketing", "manager"])
    const writer = useRoleUsers(["manager", "processer"])
    const assigner = useRoleUsers(["processer"])
    const {trademarkType, trademarkStatus, writeStatus, trademarkKind} = allDict
    const {setToast} = useGlobalActions()
    const {write, id: drawerId} = useDrawerModal()
    const {mutateAsync: createTrademarkMutate, isPending: createTrademarkPending} = useCreateTrademark()
    const {mutateAsync: updateTrademarkMutate, isPending: updateTrademarkPending} = useUpdateTrademark()
    const {data: trademarkData} = useQueryTrademark({id: drawerId!}, isNumber(drawerId))
    const trademark = trademarkData?.data
    const {onSearch} = useDrawerModal()
    const {setDrawerModal} = useGlobalActions()

    const initValues = () => {
        if (write == 'edit') {
            return {
                ...trademark, applyDate: iso2Date(trademark?.applyDate),
                writeStatusTime: iso2Date(trademark?.writeStatusTime), registerDate: iso2Date(trademark?.registerDate),
                initialDate: iso2Date(trademark?.initialDate), startRightsDate: iso2Date(trademark?.startRightsDate),
                endRightsDate: iso2Date(trademark?.endRightsDate), rightsDate: [iso2Date(trademark?.startRightsDate),
                    iso2Date(trademark?.endRightsDate)], contractName: trademark?.contract?.contractName,
                tempWriter: {username: trademark?.writer, id: trademark?.writerId},
                tempSeller: {username: trademark?.seller, id: trademark?.sellerId},
                file: isEmpty(trademark?.pattern) ? [] : [{name: "商标图样", url: trademark?.pattern}],
                insideDeadline: iso2Date(trademark?.insideDeadline)
            };
        }
        return {}
    }

    const {
        register,
        control,
        setValue,
        handleSubmit,
        reset,
        formState: {errors},
    } = useForm<WriteTrademarkForm>({
        values: initValues() as WriteTrademarkForm,
        resolver: zodResolver(validationSchema),
        defaultValues,
    });

    const onSubmit = async (values: WriteTrademarkForm) => {
        const result = write == 'add' ? await createTrademarkMutate(values) : await updateTrademarkMutate({...values, id: drawerId!})
        if (result.code == 0) {
            onSearch && onSearch()
            setDrawerModal({open: false})
            setToast({open: true, message: result.msg, severity: "success", variant: 'standard'})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                <Grid size={6}>
                    <InputFile<WriteTrademarkForm> maxTagWidth={'200px'} label={'商标图样'} tag={0} isSave={false} uploadCallback={(file: UploadFileVO) => {
                        setValue("pattern", file?.url
                        )
                    }} mode={'single'} targetId={drawerId} attr={register("file")} control={control}
                    />
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>服务类型</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            options={trademarkKind}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue("kindId", option?.value)
                                onChange(option?.label);
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.kind}
                                           helperText={errors?.kind?.message}
                                           variant="outlined"
                                           placeholder={"选择服务类型"} {...params} />
                            )}
                        />} name={"kind"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>商标名称</FormLabel>
                        <TextField
                            {...register("trademarkName")}
                            error={!!errors?.trademarkName}
                            helperText={errors?.trademarkName?.message}
                            slotProps={{
                                input: {
                                    inputProps: {maxLength: 60},
                                },
                            }}
                            placeholder="填写专利名称"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>商标类别</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            options={trademarkType}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue("categoryId", option?.value)
                                onChange(option?.label);
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.kind}
                                           helperText={errors?.kind?.message}
                                           variant="outlined"
                                           placeholder={"选择商标类别"} {...params} />
                            )}
                        />} name={"category"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>申请日期</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                            <DatePicker
                                showYearDropdown={true}
                                showMonthDropdown={true}
                                dateFormat="yyyy/MM/dd"
                                selected={value}
                                onChange={date => onChange(date)}
                                locale='zhCN'
                                placeholderText={"选择申请日期"}
                                customInput={<TextField
                                    error={!!errors?.applyDate}
                                    helperText={errors?.applyDate?.message}
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                />}
                            />
                        </DatePickers>} name={'applyDate'}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>登记号</FormLabel>
                        <TextField
                            {...register("applyNo")}
                            error={!!errors?.applyNo}
                            helperText={errors?.applyNo?.message}
                            slotProps={{
                                input: {
                                    inputProps: {maxLength: 60},
                                },
                            }}
                            placeholder="填写登记号"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>商标状态</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            options={trademarkStatus}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue("trademarkStatusId", option?.value)
                                onChange(option?.label);
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.trademarkStatus}
                                           helperText={errors?.trademarkStatus?.message}
                                           variant="outlined"
                                           placeholder={"选择商标状态"} {...params} />
                            )}
                        />} name={"trademarkStatus"}/>
                    </FormControl>
                </Grid>

                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>初审公告日期</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                            <DatePicker
                                showYearDropdown={true}
                                showMonthDropdown={true}
                                dateFormat="yyyy/MM/dd"
                                selected={value as Date}
                                onChange={date => onChange(date)}
                                locale='zhCN'
                                placeholderText={"选择初审公告日期"}
                                customInput={<TextField
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                />}
                            />
                        </DatePickers>} name={'initialDate'}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>初审公告号</FormLabel>
                        <TextField
                            {...register("initialNo")}

                            slotProps={{
                                input: {
                                    inputProps: {maxLength: 60},
                                },
                            }}
                            placeholder="填写初审公告号"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>注册公告日期</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                            <DatePicker
                                showYearDropdown={true}
                                showMonthDropdown={true}
                                dateFormat="yyyy/MM/dd"
                                selected={value as Date}
                                onChange={date => onChange(date)}
                                locale='zhCN'
                                placeholderText={"选择注册公告日期"}
                                customInput={<TextField
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                />}
                            />
                        </DatePickers>} name={'registerDate'}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>注册公告号</FormLabel>
                        <TextField
                            {...register("registerNo")}
                            slotProps={{
                                input: {
                                    inputProps: {maxLength: 60},
                                },
                            }}
                            placeholder="填写注册公告号"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel>专有权起止时间</FormLabel>
                        <DatePickers>
                            <Controller control={control} render={({field: {onChange, value}}) => {
                                const [startDate, endDate] = value || []
                                return <DatePicker
                                    showYearDropdown={true}
                                    selectsRange={true}
                                    showMonthDropdown={true}
                                    startDate={startDate}
                                    endDate={endDate}
                                    dateFormat="yyyy/MM/dd"
                                    onChange={(date: [Date | null, Date | null]) => {
                                        const [startRightsDate, endRightsDate] = date
                                        setValue("startRightsDate", startRightsDate)
                                        setValue("endRightsDate", endRightsDate)
                                        onChange(date)
                                    }}
                                    locale='zhCN'
                                    placeholderText={"选择专用权起止时间"}
                                    customInput={<TextField fullWidth={true} sx={{
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                    />}
                                />
                            }} name={'rightsDate'}/>
                        </DatePickers>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>分配人</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <Autocomplete
                                options={assigner}
                                value={value}
                                onChange={(_, option: RoleUserVO | null) => {
                                    setValue("assignerId", option!.id)
                                    setValue("assigner", option?.username)
                                    onChange(option)
                                }}
                                isOptionEqualToValue={(option, value) => option.id == value.id}
                                getOptionKey={option => option.username}
                                getOptionLabel={option => option.username}
                                fullWidth
                                renderInput={(params) => (
                                    <TextField error={!!errors?.assignerId}
                                               helperText={errors?.assignerId?.message}
                                               variant="outlined"
                                               placeholder={"选择分配人"} {...params} />
                                )}
                            />
                        }} name={"tempAssigner"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <SearchData<WriteTrademarkForm, CustomerPO> control={control} attr={"customerName"} attrId={"customerId"} errMsg={errors.customerName?.message}
                                                                setValue={setValue}
                                                                label={"关联客户"}/>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>业务人员</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <Autocomplete
                                options={seller}
                                value={value}
                                onChange={(_, option: RoleUserVO | null) => {
                                    setValue("sellerId", option!.id)
                                    setValue("seller", option?.username)
                                    onChange(option)
                                }}
                                isOptionEqualToValue={(option, value) => option.id == value.id}
                                getOptionKey={option => option.username}
                                getOptionLabel={option => option.username}
                                fullWidth
                                renderInput={(params) => (
                                    <TextField error={!!errors?.sellerId}
                                               helperText={errors?.sellerId?.message}
                                               variant="outlined"
                                               placeholder={"选择业务人员"} {...params} />
                                )}
                            />
                        }} name={"tempSeller"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <SearchData<WriteTrademarkForm, SearchContractVO> control={control} tag={2} attr={"contractName"} autoLoad={true} attrId={"contractId"} setValue={setValue}
                                                                      label={"关联合同"}/>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel>价格</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <TextField
                            value={value}
                            onChange={event => onChange(event.target.value)}
                            slotProps={{
                                input: {
                                    inputProps: {
                                        min: 0,
                                        max: 1000000,
                                        maxLength: 8, inputMode: 'numeric', pattern: '[0-9]*'
                                    },
                                    endAdornment: <CurrencyYuanOutlinedIcon color="disabled"/>,
                                },
                            }}
                            placeholder="填写价格"
                            variant="outlined"
                        />} name={'price'}/>
                    </FormControl>
                </Grid>

                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>撰稿人</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <Autocomplete
                                options={writer}
                                value={value}
                                onChange={(_, option: RoleUserVO | null) => {
                                    setValue("writerId", option!.id)
                                    setValue("writer", option?.username)
                                    onChange(option)
                                }}
                                isOptionEqualToValue={(option, value) => option.id == value.id}
                                getOptionKey={option => option.username}
                                getOptionLabel={option => option.username}
                                fullWidth
                                renderInput={(params) => (
                                    <TextField error={!!errors?.writerId}
                                               helperText={errors?.writerId?.message}
                                               variant="outlined"
                                               placeholder={"选择撰稿人"} {...params} />
                                )}
                            />
                        }} name={"tempWriter"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>撰写状态</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            options={writeStatus}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue("writeStatusId", option?.value)
                                onChange(option?.label);
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.writeStatus}
                                           helperText={errors?.writeStatus?.message}
                                           variant="outlined"
                                           placeholder={"选择撰写状态"} {...params} />
                            )}
                        />} name={"writeStatus"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel>内部期限</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                            <DatePicker
                                showYearDropdown={true}
                                showMonthDropdown={true}
                                dateFormat="yyyy/MM/dd"
                                selected={value as Date}
                                onChange={date => onChange(date)}
                                locale='zhCN'
                                placeholderText={"选择内部期限"}
                                customInput={<TextField
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                />}
                            />
                        </DatePickers>} name={'insideDeadline'}/>
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth={true}>
                        <FormLabel>商品或服务项目</FormLabel>
                        <TextField
                            multiline
                            rows={3}
                            {...register("scope")}
                            slotProps={{
                                input: {
                                    sx: {height: 'auto', minHeight: 80},
                                    inputProps: {
                                        maxLength: 250,
                                    },
                                },
                            }}
                            placeholder="输入商品或服务项目"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth={true}>
                        <FormLabel>备注</FormLabel>
                        <TextField
                            multiline
                            rows={3}
                            {...register("explain")}
                            slotProps={{
                                input: {
                                    sx: {height: 'auto', minHeight: 80},
                                    inputProps: {
                                        maxLength: 250,
                                    },
                                },
                            }}
                            placeholder="输入备注"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <DrawerFooter loading={createTrademarkPending || updateTrademarkPending} onReset={() => reset(defaultValues)}/>
        </Box>
    )
        ;
};
